'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo, useCallback } from 'react';
import { useI18n } from '@udemy-v2/i18n';
import { useClpPurchaseContext } from '../context/clp-purchase-context.react-component';
import { useClpCourse } from '../context/clp-course-provider.react-component';
import { publishEvent } from '@udemy-v2/event-tracking';
import { BuyNowEvent, EnrollNowEvent, LearningProduct } from '../events/events';
import { useOneClickCheckoutContext } from '@/lib/hooks/use-one-click-checkout-context';
import { OneClickCheckoutContext } from '@/types/one-click-checkout-context';
import { type BuyButtonStyle } from '../context/clp-purchase-context.react-component';
import { type IconName } from '@udemy-v2/icons';
import { useOrgPrimaryCtaButton } from './use-org-primary-cta-button';
import { useUserHasOrganization } from '@/lib/hooks/use-user-has-organization';
export const enum BuyButtonEventType {
  BUY_NOW = 'buy_now',
  ENROLL_NOW = 'enroll_now',
  GO_TO_COURSE = 'go_to_course',
}
export interface BuyButtonState {
  text?: string;
  checkoutUrl: string;
  trackEvent: () => void;
  isLoading: boolean;
  oneClickCheckoutContext?: OneClickCheckoutContext;
  udStyle?: BuyButtonStyle;
  iconName?: IconName;
}

/**
 * Determines the state and behavior of the CLP primary CTA button across all user contexts:
 * enrolled, personal plan subscriber, organization member, and transactional (free/paid).
 */
export const useClpPrimaryCtaButton = () => {
  const $ = _c(57);
  const {
    gettext
  } = useI18n();
  const hasOrganization = useUserHasOrganization();
  const purchaseContext = useClpPurchaseContext();
  const course = useClpCourse();
  const {
    data: oneClickCheckoutContext,
    isLoading: isOneClickCheckoutLoading
  } = useOneClickCheckoutContext(Number(course.id));
  const orgState = useOrgPrimaryCtaButton();
  let isMobileApp;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    const currentParams = new URLSearchParams(window.location.search);
    isMobileApp = currentParams.get("display_type") === "mobile_app";
    t0 = currentParams.get("couponCode");
    $[0] = isMobileApp;
    $[1] = t0;
  } else {
    isMobileApp = $[0];
    t0 = $[1];
  }
  const couponCode = t0;
  const courseId = course?.id;
  let t1;
  if ($[2] !== courseId) {
    t1 = (baseUrl, t2) => {
      const options = t2 === undefined ? {} : t2;
      const url = new URL(baseUrl, window.location.origin);
      const params = {
        ref: options.source || "course_landing_page",
        checkout_type: "express",
        return_path: window.location.pathname,
        ...(isMobileApp && {
          display_type: "mobile_app"
        }),
        ...(courseId && {
          course_id: courseId
        }),
        ...(options.referrer && {
          referrer: options.referrer
        }),
        ...(couponCode && {
          couponCode
        }),
        ...(options.tracking || {})
      };
      Object.entries(params).forEach(t3 => {
        const [key, value] = t3;
        url.searchParams.set(key, value);
      });
      return url.toString();
    };
    $[2] = courseId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const buildCheckoutUrl = t1;
  const courseId_0 = Number(course.id);
  const courseTrackingId = course.frontendTrackingId || course.id;
  let t2;
  if ($[4] !== courseId_0 || $[5] !== courseTrackingId) {
    t2 = {
      buyable: {
        id: courseId_0,
        type: LearningProduct.COURSE,
        trackingId: courseTrackingId
      }
    };
    $[4] = courseId_0;
    $[5] = courseTrackingId;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const eventPayload = t2;
  let t3;
  bb0: {
    const isLoading = purchaseContext.isLoading || isOneClickCheckoutLoading;
    if (isLoading) {
      let t4;
      if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = {
          checkoutUrl: "",
          trackEvent: _temp,
          isLoading: true
        };
        $[7] = t4;
      } else {
        t4 = $[7];
      }
      t3 = t4;
      break bb0;
    }
    if (hasOrganization) {
      t3 = orgState;
      break bb0;
    }
    if (purchaseContext.isConsumerSubscriptionPaused) {
      let t4;
      if ($[8] !== gettext) {
        t4 = gettext("Re-activate subscription");
        $[8] = gettext;
        $[9] = t4;
      } else {
        t4 = $[9];
      }
      let t5;
      if ($[10] !== t4) {
        t5 = {
          text: t4,
          checkoutUrl: "/user/manage-subscriptions?reactivate=1",
          trackEvent: _temp2,
          isLoading: false
        };
        $[10] = t4;
        $[11] = t5;
      } else {
        t5 = $[11];
      }
      t3 = t5;
      break bb0;
    }
    if (!purchaseContext.isPublishedAndNonPrivate) {
      let t4;
      if ($[12] !== gettext) {
        t4 = gettext("Unavailable");
        $[12] = gettext;
        $[13] = t4;
      } else {
        t4 = $[13];
      }
      let t5;
      if ($[14] !== t4) {
        t5 = {
          text: t4,
          checkoutUrl: "",
          trackEvent: _temp3,
          isLoading: false
        };
        $[14] = t4;
        $[15] = t5;
      } else {
        t5 = $[15];
      }
      t3 = t5;
      break bb0;
    }
    if (purchaseContext.isEnrolledInCourse) {
      let t4;
      if ($[16] !== gettext) {
        t4 = gettext("Go to course");
        $[16] = gettext;
        $[17] = t4;
      } else {
        t4 = $[17];
      }
      const t5 = course.urlCourseTaking ?? "";
      let t6;
      if ($[18] !== t4 || $[19] !== t5) {
        t6 = {
          text: t4,
          checkoutUrl: t5,
          trackEvent: _temp4,
          isLoading: false
        };
        $[18] = t4;
        $[19] = t5;
        $[20] = t6;
      } else {
        t6 = $[20];
      }
      t3 = t6;
      break bb0;
    }
    if (purchaseContext.isPersonalPlanSubscriber && purchaseContext.isCourseIncludedInUserSubscription) {
      let t4;
      if ($[21] !== gettext) {
        t4 = gettext("Enroll now");
        $[21] = gettext;
        $[22] = t4;
      } else {
        t4 = $[22];
      }
      const t5 = course.urlCourseTaking ?? "";
      let t6;
      if ($[23] !== t4 || $[24] !== t5) {
        t6 = {
          text: t4,
          checkoutUrl: t5,
          trackEvent: _temp5,
          isLoading: false
        };
        $[23] = t4;
        $[24] = t5;
        $[25] = t6;
      } else {
        t6 = $[25];
      }
      t3 = t6;
      break bb0;
    }
    if (purchaseContext.isFree) {
      let t4;
      if ($[26] !== gettext) {
        t4 = gettext("Enroll now");
        $[26] = gettext;
        $[27] = t4;
      } else {
        t4 = $[27];
      }
      const t5 = course.urlAutoEnroll ?? "";
      let t6;
      if ($[28] !== eventPayload) {
        t6 = () => publishEvent(new EnrollNowEvent(eventPayload));
        $[28] = eventPayload;
        $[29] = t6;
      } else {
        t6 = $[29];
      }
      let t7;
      if ($[30] !== t4 || $[31] !== t5 || $[32] !== t6) {
        t7 = {
          text: t4,
          checkoutUrl: t5,
          trackEvent: t6,
          isLoading: false
        };
        $[30] = t4;
        $[31] = t5;
        $[32] = t6;
        $[33] = t7;
      } else {
        t7 = $[33];
      }
      t3 = t7;
      break bb0;
    }
    const baseCheckoutUrl = `/payment/checkout/express/course/${course.id}/`;
    if (purchaseContext.isFreeWithDiscount) {
      let t4;
      if ($[34] !== gettext) {
        t4 = gettext("Enroll now");
        $[34] = gettext;
        $[35] = t4;
      } else {
        t4 = $[35];
      }
      let t5;
      if ($[36] !== baseCheckoutUrl || $[37] !== buildCheckoutUrl) {
        t5 = buildCheckoutUrl(baseCheckoutUrl);
        $[36] = baseCheckoutUrl;
        $[37] = buildCheckoutUrl;
        $[38] = t5;
      } else {
        t5 = $[38];
      }
      let t6;
      if ($[39] !== eventPayload) {
        t6 = () => publishEvent(new EnrollNowEvent(eventPayload));
        $[39] = eventPayload;
        $[40] = t6;
      } else {
        t6 = $[40];
      }
      let t7;
      if ($[41] !== t4 || $[42] !== t5 || $[43] !== t6) {
        t7 = {
          text: t4,
          checkoutUrl: t5,
          trackEvent: t6,
          isLoading: false
        };
        $[41] = t4;
        $[42] = t5;
        $[43] = t6;
        $[44] = t7;
      } else {
        t7 = $[44];
      }
      t3 = t7;
      break bb0;
    }
    let t4;
    if ($[45] !== gettext) {
      t4 = gettext("Buy now");
      $[45] = gettext;
      $[46] = t4;
    } else {
      t4 = $[46];
    }
    let t5;
    if ($[47] !== baseCheckoutUrl || $[48] !== buildCheckoutUrl) {
      t5 = buildCheckoutUrl(baseCheckoutUrl);
      $[47] = baseCheckoutUrl;
      $[48] = buildCheckoutUrl;
      $[49] = t5;
    } else {
      t5 = $[49];
    }
    let t6;
    if ($[50] !== eventPayload) {
      t6 = () => publishEvent(new BuyNowEvent(eventPayload));
      $[50] = eventPayload;
      $[51] = t6;
    } else {
      t6 = $[51];
    }
    let t7;
    if ($[52] !== oneClickCheckoutContext || $[53] !== t4 || $[54] !== t5 || $[55] !== t6) {
      t7 = {
        text: t4,
        checkoutUrl: t5,
        trackEvent: t6,
        isLoading: false,
        oneClickCheckoutContext
      };
      $[52] = oneClickCheckoutContext;
      $[53] = t4;
      $[54] = t5;
      $[55] = t6;
      $[56] = t7;
    } else {
      t7 = $[56];
    }
    t3 = t7;
  }
  return t3;
};
function _temp() {}
function _temp2() {}
function _temp3() {}
function _temp4() {}
function _temp5() {}